<script setup lang="ts">
import { computed } from "vue";
import { useCart } from "../composables/useCart";
import { formatPrice } from "../data/products";
import ProductArt from "./ProductArt.vue";

const cart = useCart();

const hasItems = computed(() => cart.state.lines.length > 0);
const shipping = computed(() => (cart.subtotal.value > 180 ? 0 : 12));
const total = computed(() => cart.subtotal.value + shipping.value);
</script>

<template>
  <Transition name="drawer">
    <aside v-if="cart.state.open" class="drawer" role="dialog" aria-modal="true" aria-label="Bag">
      <div class="drawer__head">
        <div>
          <span class="os1-eyebrow">OS1 Bag</span>
          <h3 class="drawer__title">Your selection</h3>
        </div>
        <button class="drawer__close" @click="cart.close" aria-label="Close bag">
          <svg viewBox="0 0 20 20" width="14" height="14" fill="none">
            <path d="M4 4l12 12M16 4L4 16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div v-if="hasItems" class="drawer__items">
        <article v-for="line in cart.state.lines" :key="`${line.product.id}-${line.size}-${line.swatch}`" class="line">
          <div class="line__art">
            <ProductArt :product="line.product" aspect="1 / 1" />
          </div>
          <div class="line__body">
            <div class="line__top">
              <span class="line__name">{{ line.product.name }}</span>
              <span class="line__price">{{ formatPrice(line.product.price * line.quantity) }}</span>
            </div>
            <span class="line__meta">{{ line.swatch }} · Size {{ line.size }}</span>
            <div class="line__controls">
              <div class="qty">
                <button @click="cart.setQuantity(line, line.quantity - 1)" aria-label="Decrease">−</button>
                <span>{{ line.quantity }}</span>
                <button @click="cart.setQuantity(line, line.quantity + 1)" aria-label="Increase">+</button>
              </div>
              <button class="line__remove" @click="cart.remove(line)">Remove</button>
            </div>
          </div>
        </article>
      </div>

      <div v-else class="drawer__empty">
        <p>The bag is quiet. Add a piece and it will sit here, waiting.</p>
      </div>

      <footer class="drawer__foot">
        <div class="drawer__row">
          <span>Subtotal</span>
          <span class="drawer__amount">{{ formatPrice(cart.subtotal.value) }}</span>
        </div>
        <div class="drawer__row">
          <span>Shipping</span>
          <span class="drawer__amount">
            {{ shipping === 0 ? "Free" : formatPrice(shipping) }}
          </span>
        </div>
        <div class="drawer__row drawer__row--total">
          <span>Total</span>
          <span class="drawer__amount">{{ formatPrice(total) }}</span>
        </div>
        <button class="os1-button os1-button--solid drawer__cta" :disabled="!hasItems">
          Continue to checkout
        </button>
        <p class="drawer__note">
          Checkout opens in the OS1 desktop app for a calmer flow. Don't have it yet?
          <a href="#install">Install OS1.</a>
        </p>
      </footer>
    </aside>
  </Transition>
  <Transition name="overlay">
    <div v-if="cart.state.open" class="drawer__overlay" @click="cart.close" aria-hidden="true" />
  </Transition>
</template>

<style scoped>
.drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(420px, 96vw);
  z-index: 90;
  background: linear-gradient(180deg, #1c0808 0%, #110404 100%);
  border-left: 1px solid var(--os1-line-strong);
  display: flex;
  flex-direction: column;
  box-shadow: -30px 0 80px -30px rgba(0, 0, 0, 0.7);
}

.drawer__overlay {
  position: fixed;
  inset: 0;
  z-index: 85;
  background: rgba(6, 1, 1, 0.5);
  backdrop-filter: blur(4px);
}

.drawer__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.4rem 1.5rem 1rem;
  border-bottom: 1px solid var(--os1-line);
}

.drawer__title {
  font-size: 1.3rem;
  color: var(--os1-paper);
  margin-top: 0.3rem;
}

.drawer__close {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  border: 1px solid var(--os1-line-strong);
  color: var(--os1-ink-soft);
  display: grid;
  place-items: center;
}

.drawer__items {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.line {
  display: grid;
  grid-template-columns: 88px 1fr;
  gap: 0.9rem;
}

.line__art {
  border-radius: var(--os1-radius);
  overflow: hidden;
}

.line__body {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.line__top {
  display: flex;
  justify-content: space-between;
  gap: 0.4rem;
}

.line__name {
  font-family: var(--os1-font-serif);
  color: var(--os1-paper);
  font-size: 0.98rem;
}

.line__price {
  font-family: var(--os1-font-mono);
  color: var(--os1-rose);
  font-size: 0.82rem;
}

.line__meta {
  color: var(--os1-ink-mute);
  font-size: 0.72rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.line__controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.3rem;
}

.qty {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.55rem;
  border: 1px solid var(--os1-line);
  border-radius: 999px;
}

.qty button {
  color: var(--os1-ink-soft);
  width: 18px;
  height: 18px;
  display: grid;
  place-items: center;
}

.qty button:hover {
  color: var(--os1-rose);
}

.qty span {
  font-family: var(--os1-font-mono);
  font-size: 0.78rem;
  min-width: 1ch;
  text-align: center;
}

.line__remove {
  color: var(--os1-ink-mute);
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.line__remove:hover {
  color: var(--os1-red);
}

.drawer__empty {
  flex: 1;
  display: grid;
  place-items: center;
  padding: 2rem;
  text-align: center;
  color: var(--os1-ink-mute);
  font-style: italic;
}

.drawer__foot {
  border-top: 1px solid var(--os1-line);
  padding: 1.3rem 1.5rem 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.drawer__row {
  display: flex;
  justify-content: space-between;
  color: var(--os1-ink-soft);
  font-size: 0.85rem;
}

.drawer__row--total {
  color: var(--os1-paper);
  font-size: 1rem;
  margin-top: 0.2rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--os1-line);
}

.drawer__amount {
  font-family: var(--os1-font-mono);
}

.drawer__cta {
  margin-top: 0.8rem;
}

.drawer__cta:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.drawer__note {
  color: var(--os1-ink-mute);
  font-size: 0.72rem;
  margin-top: 0.4rem;
}

.drawer__note a {
  color: var(--os1-rose);
  border-bottom: 1px solid currentColor;
}

.drawer-enter-active,
.drawer-leave-active {
  transition: transform 280ms ease;
}
.drawer-enter-from,
.drawer-leave-to {
  transform: translateX(100%);
}

.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 200ms ease;
}
.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}
</style>
